import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileVideoIcon } from '@phosphor-icons/react';
import { Popover } from '@/components/Popover';
import { cn } from '@/lib/utils';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { useSourcesStore } from '@/stores';
import {
    pruneFoldersContaining,
    type SelectedFolders,
} from '@/stores/sources.store';
import { useFeatureFlags } from '@/hooks';
import {
    FullSource,
    SourceType,
    getSelectableSourceType,
} from '@/types/source';
import { SearchInput, SegmentedControl } from '@/components';
import { SecurityCameraIconMirrored } from '@/utils/icons';
import {
    SourcesGrid,
    SourcesMenuFooter,
    SourcesMenuSidebar,
    type SidebarGroupView,
} from './components';
import { BUILT_IN_TYPE_FOLDER_IDS, useSourcesData } from './hooks';

export interface SourceSelectionOverride {
    selected: Set<string>;
    onToggle: (id: string) => void;
    onSelect: (id: string) => void;
    onDeselect: (id: string) => void;
    onSelectMany?: (ids: string[]) => void;
    onDeselectMany?: (ids: string[]) => void;
    onSelectFolder?: (folderId: string, memberIds: string[]) => void;
}

interface SourcesMenuProps {
    includeVideos?: boolean;
    onClose?: () => void;
    selectionOverride?: SourceSelectionOverride;
    submitLabel?: string;
    requireSelection?: boolean;
    variant?: 'popover' | 'inline';
    isSourceDisabled?: (id: string) => boolean;
    disabledSourceMessage?: string;
    maxAdditionalSources?: number | null;
}

export function SourcesMenu({
    includeVideos = true,
    onClose,
    selectionOverride,
    submitLabel,
    isSourceDisabled,
    disabledSourceMessage,
    maxAdditionalSources = null,
    requireSelection = false,
    variant = 'popover',
}: SourcesMenuProps = {}) {
    const { t } = useTranslation(I18nNamespace.SOURCES);
    const storeSelected = useSourcesStore((s) => s.selected);
    const storeSelectedFolders = useSourcesStore((s) => s.selectedFolders);
    const setSelectedSources = useSourcesStore((s) => s.setSelectedSources);
    const sourcesMap = useSourcesStore((s) => s.sourcesMap);
    const { liveCamerasEnabled, hdmiEnabled, videoFilesEnabled } =
        useFeatureFlags();

    const filesSelectable = includeVideos && videoFilesEnabled;
    const camerasSelectable = liveCamerasEnabled || hdmiEnabled;
    const [sourceTypeFilter, setSourceTypeFilter] = useState<SourceType>(() =>
        filesSelectable ? SourceType.FILE : SourceType.CAMERA,
    );
    const [hasResolvedDefaultSourceType, setHasResolvedDefaultSourceType] =
        useState(false);
    const typeFilterAvailable = filesSelectable && camerasSelectable;

    const selectSourceType = useCallback((type: SourceType) => {
        setHasResolvedDefaultSourceType(true);
        setSourceTypeFilter(type);
    }, []);

    useEffect(() => {
        if (
            sourceTypeFilter === SourceType.FILE &&
            !filesSelectable &&
            camerasSelectable
        ) {
            setSourceTypeFilter(SourceType.CAMERA);
        }
        if (
            sourceTypeFilter === SourceType.CAMERA &&
            !camerasSelectable &&
            filesSelectable
        ) {
            setSourceTypeFilter(SourceType.FILE);
        }
    }, [sourceTypeFilter, filesSelectable, camerasSelectable]);

    const [localSelected, setLocalSelected] = useState<Set<string>>(
        () => new Set(storeSelected),
    );
    const [localSelectedFolders, setLocalSelectedFolders] =
        useState<SelectedFolders>(() => new Map(storeSelectedFolders));
    const [initialState] = useState(() => ({
        selected: new Set(storeSelected),
        folders: new Map(storeSelectedFolders),
    }));

    const localToggle = useCallback((id: string) => {
        setLocalSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
        setLocalSelectedFolders((prev) => pruneFoldersContaining(prev, [id]));
    }, []);

    const localSelectMany = useCallback((ids: string[]) => {
        setLocalSelected((prev) => {
            const next = new Set(prev);
            ids.forEach((id) => next.add(id));
            return next;
        });
    }, []);

    const localDeselectMany = useCallback((ids: string[]) => {
        setLocalSelected((prev) => {
            const next = new Set(prev);
            ids.forEach((id) => next.delete(id));
            return next;
        });
        setLocalSelectedFolders((prev) => pruneFoldersContaining(prev, ids));
    }, []);

    const localSelectFolder = useCallback(
        (folderId: string, memberIds: string[]) => {
            setLocalSelected((prev) => {
                const next = new Set(prev);
                memberIds.forEach((id) => next.add(id));
                return next;
            });
            setLocalSelectedFolders((prev) =>
                new Map(prev).set(folderId, memberIds),
            );
        },
        [],
    );

    const selected = selectionOverride?.selected ?? localSelected;
    const rawToggleSource = selectionOverride?.onToggle ?? localToggle;

    const rawSelectMany = useCallback(
        (ids: string[]) => {
            if (selectionOverride?.onSelectMany) {
                selectionOverride.onSelectMany(ids);
            } else if (selectionOverride) {
                ids.forEach(selectionOverride.onSelect);
            } else {
                localSelectMany(ids);
            }
        },
        [selectionOverride, localSelectMany],
    );

    const rawDeselectMany = useCallback(
        (ids: string[]) => {
            if (selectionOverride?.onDeselectMany) {
                selectionOverride.onDeselectMany(ids);
            } else if (selectionOverride) {
                ids.forEach(selectionOverride.onDeselect);
            } else {
                localDeselectMany(ids);
            }
        },
        [selectionOverride, localDeselectMany],
    );

    const rawSelectFolder = useCallback(
        (folderId: string, memberIds: string[]) => {
            if (selectionOverride?.onSelectFolder) {
                selectionOverride.onSelectFolder(folderId, memberIds);
            } else if (selectionOverride) {
                rawSelectMany(memberIds);
            } else {
                localSelectFolder(folderId, memberIds);
            }
        },
        [selectionOverride, rawSelectMany, localSelectFolder],
    );

    const {
        searchQuery,
        setSearchQuery,
        activeFolderId,
        setActiveFolderId,
        folders,
        activeFolderView,
        filteredSources,
        builtInTypeSources,
        hasMoreFolders,
        loadMoreFolders,
        isLoadingMoreFolders,
        folderSourcesByFolderId,
        scopeSourceIdsByScopeId,
        activeScopeSearchSourceIds,
        camerasTotal,
        videosTotal,
        isLoadingSourceTotals,
        hasSourceTotalsError,
    } = useSourcesData({
        includeVideos: sourceTypeFilter === SourceType.FILE,
        includeCameras: sourceTypeFilter === SourceType.CAMERA,
        excludeEmptyFolders: true,
    });

    useEffect(() => {
        if (hasResolvedDefaultSourceType) return;
        if (!typeFilterAvailable) return;
        if (sourceTypeFilter !== SourceType.FILE) return;
        if (isLoadingSourceTotals || hasSourceTotalsError) return;
        setHasResolvedDefaultSourceType(true);
        if (videosTotal === 0 && camerasTotal > 0) {
            setSourceTypeFilter(SourceType.CAMERA);
        }
    }, [
        hasResolvedDefaultSourceType,
        typeFilterAvailable,
        sourceTypeFilter,
        isLoadingSourceTotals,
        hasSourceTotalsError,
        videosTotal,
        camerasTotal,
    ]);

    const sourceTypeById = useMemo(() => {
        const map = new Map<string, SourceType>();
        const register = (list: readonly FullSource[]) =>
            list.forEach((source) =>
                map.set(source.id, getSelectableSourceType(source.type)),
            );
        Object.values(builtInTypeSources).forEach(register);
        Object.values(folderSourcesByFolderId).forEach(register);
        register(activeFolderView.sources);
        return map;
    }, [builtInTypeSources, folderSourcesByFolderId, activeFolderView.sources]);

    const getSourceType = useCallback(
        (id: string): SourceType | undefined => {
            const loadedType = sourceTypeById.get(id);
            if (loadedType) return loadedType;
            const storeSource = sourcesMap.get(id);
            return storeSource
                ? getSelectableSourceType(storeSource.type)
                : undefined;
        },
        [sourceTypeById, sourcesMap],
    );

    const completeCurrentTypeIds = useMemo<Set<string> | null>(() => {
        const scopeIdLists = Object.values(scopeSourceIdsByScopeId);
        if (scopeIdLists.length === 0) return null;
        return new Set(scopeIdLists.flat());
    }, [scopeSourceIdsByScopeId]);

    const clearOppositeTypeSelections = useCallback(
        (incomingType: SourceType) => {
            const keepIds =
                incomingType === sourceTypeFilter
                    ? completeCurrentTypeIds
                    : null;
            const oppositeIds: string[] = [];
            selected.forEach((id) => {
                if (keepIds) {
                    if (!keepIds.has(id)) oppositeIds.push(id);
                    return;
                }
                const sourceType = getSourceType(id);
                if (sourceType && sourceType !== incomingType) {
                    oppositeIds.push(id);
                }
            });
            if (oppositeIds.length > 0) {
                rawDeselectMany(oppositeIds);
            }
        },
        [
            selected,
            sourceTypeFilter,
            completeCurrentTypeIds,
            getSourceType,
            rawDeselectMany,
        ],
    );

    const toggleSource = useCallback(
        (id: string) => {
            if (!selected.has(id)) {
                const sourceType = getSourceType(id);
                if (sourceType) clearOppositeTypeSelections(sourceType);
            }
            rawToggleSource(id);
        },
        [selected, getSourceType, clearOppositeTypeSelections, rawToggleSource],
    );

    const selectSources = useCallback(
        (ids: string[]) => {
            const incomingType = ids
                .map((id) => getSourceType(id))
                .find((type): type is SourceType => Boolean(type));
            if (incomingType) clearOppositeTypeSelections(incomingType);
            rawSelectMany(ids);
        },
        [getSourceType, clearOppositeTypeSelections, rawSelectMany],
    );

    const selectFolderSources = useCallback(
        (folderId: string, memberIds: string[]) => {
            const incomingType = memberIds
                .map((id) => getSourceType(id))
                .find((type): type is SourceType => Boolean(type));
            if (incomingType) clearOppositeTypeSelections(incomingType);
            rawSelectFolder(folderId, memberIds);
        },
        [getSourceType, clearOppositeTypeSelections, rawSelectFolder],
    );

    const deselectSources = rawDeselectMany;

    const hasChanges = useMemo(() => {
        if (localSelected.size !== initialState.selected.size) return true;
        if (
            Array.from(localSelected).some(
                (id) => !initialState.selected.has(id),
            )
        ) {
            return true;
        }
        if (localSelectedFolders.size !== initialState.folders.size) {
            return true;
        }
        return Array.from(localSelectedFolders.keys()).some(
            (folderId) => !initialState.folders.has(folderId),
        );
    }, [localSelected, localSelectedFolders, initialState]);

    const handleAddSources = () => {
        setSelectedSources(localSelected, new Map(localSelectedFolders));
        onClose?.();
    };

    const scopeIdsByScopeId = useMemo<Record<string, readonly string[]>>(() => {
        const result: Record<string, readonly string[]> = {};
        folders.forEach((group) => {
            const scopeItem = group.items.find((item) =>
                BUILT_IN_TYPE_FOLDER_IDS.has(item.id),
            );
            if (!scopeItem) return;
            result[scopeItem.id] =
                scopeSourceIdsByScopeId[scopeItem.id] ??
                (builtInTypeSources[scopeItem.id] ?? []).map(
                    (source) => source.id,
                );
        });
        return result;
    }, [folders, builtInTypeSources, scopeSourceIdsByScopeId]);

    const sidebarGroups = useMemo<SidebarGroupView[]>(
        () =>
            folders.map((group) => {
                const scopeItem = group.items.find((item) =>
                    BUILT_IN_TYPE_FOLDER_IDS.has(item.id),
                );
                const scopeIds = scopeItem
                    ? (scopeIdsByScopeId[scopeItem.id] ?? [])
                    : [];
                const folderItems = group.items.filter(
                    (item) => !BUILT_IN_TYPE_FOLDER_IDS.has(item.id),
                );
                return {
                    id: group.id,
                    title: group.title,
                    scopeId: scopeItem?.id ?? group.id,
                    totalCount: scopeItem?.count ?? 0,
                    selectedCount: scopeIds.filter((id) => selected.has(id))
                        .length,
                    isLoading: scopeItem?.isLoading ?? false,
                    folders: folderItems.map((item) => {
                        const typedSources = folderSourcesByFolderId[item.id];
                        return {
                            id: item.id,
                            name: item.name,
                            totalCount: typedSources
                                ? typedSources.length
                                : item.count,
                            selectedCount:
                                typedSources?.filter((source) =>
                                    selected.has(source.id),
                                ).length ?? 0,
                            isLoading: !typedSources,
                            isSharedByOwner: item.isSharedByOwner,
                        };
                    }),
                };
            }),
        [folders, scopeIdsByScopeId, folderSourcesByFolderId, selected],
    );

    const handleToggleSelection = (folderId: string) => {
        const builtInScope = BUILT_IN_TYPE_FOLDER_IDS.has(folderId);
        const totalIds = builtInScope
            ? (scopeIdsByScopeId[folderId] ?? [])
            : (folderSourcesByFolderId[folderId] ?? []).map(
                  (source) => source.id,
              );
        if (totalIds.length === 0) return;

        const searchApplies =
            Boolean(searchQuery) && folderId === activeFolderId;
        if (searchApplies && builtInScope && !activeScopeSearchSourceIds) {
            return;
        }

        const filteredIds = searchApplies
            ? builtInScope
                ? (activeScopeSearchSourceIds ?? [])
                : filteredSources.map((source) => source.id)
            : totalIds;

        const selectedTotalCount = totalIds.filter((id) =>
            selected.has(id),
        ).length;

        if (selectedTotalCount === totalIds.length) {
            deselectSources([...totalIds]);
            return;
        }

        const selectAllExceedsCapacity = (ids: readonly string[]) =>
            maxAdditionalSources !== null &&
            ids.filter((id) => !selected.has(id)).length > maxAdditionalSources;

        if (
            selectedTotalCount === 0 &&
            filteredIds.length > 0 &&
            filteredIds.length < totalIds.length
        ) {
            if (selectAllExceedsCapacity(filteredIds)) return;
            selectSources([...filteredIds]);
            return;
        }

        if (selectAllExceedsCapacity(totalIds)) return;

        if (builtInScope) {
            selectSources([...totalIds]);
            return;
        }

        selectFolderSources(folderId, [...totalIds]);
    };

    const isInline = variant === 'inline';

    const content = (
        <>
            <div className="flex min-h-0 flex-1 flex-col gap-4 @medium:flex-row @medium:gap-6">
                <SourcesMenuSidebar
                    groups={sidebarGroups}
                    activeFolderId={activeFolderId}
                    onFolderClick={setActiveFolderId}
                    onToggleSelection={handleToggleSelection}
                    maxAdditionalSources={maxAdditionalSources}
                    onLoadMoreFolders={loadMoreFolders}
                    hasMoreFolders={hasMoreFolders}
                    isLoadingMoreFolders={isLoadingMoreFolders}
                    typeFilterSlot={
                        typeFilterAvailable ? (
                            <SegmentedControl
                                size="compact"
                                fullWidth
                                value={sourceTypeFilter}
                                onValueChange={selectSourceType}
                                options={[
                                    {
                                        value: SourceType.FILE,
                                        label: t('filters.files'),
                                        icon: <FileVideoIcon />,
                                    },
                                    {
                                        value: SourceType.CAMERA,
                                        label: t('filters.cameras'),
                                        icon: <SecurityCameraIconMirrored />,
                                    },
                                ]}
                            />
                        ) : undefined
                    }
                />

                <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                    <div className="mb-4">
                        <SearchInput
                            type="text"
                            placeholder={t('menu.searchPlaceholder', 'Search')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            autoFocus={isInline}
                            containerClassName={cn(
                                'w-full',
                                isInline && 'pt-0',
                            )}
                        />
                    </div>

                    {activeFolderView.error ? (
                        <div className="flex items-center justify-center h-full">
                            <p className="text-red-400">
                                {t('menu.loadError')}
                            </p>
                        </div>
                    ) : (
                        <SourcesGrid
                            sources={filteredSources}
                            isSourceSelected={(id) => selected.has(id)}
                            isSourceDisabled={isSourceDisabled}
                            disabledSourceMessage={disabledSourceMessage}
                            onToggleSource={toggleSource}
                            onLoadMore={activeFolderView.loadMore}
                            isLoading={activeFolderView.isLoading}
                            isLoadingMore={activeFolderView.isLoadingMore}
                            hasMore={activeFolderView.hasMore}
                        />
                    )}
                </div>
            </div>

            {!selectionOverride && (
                <SourcesMenuFooter
                    onAddSources={handleAddSources}
                    submitLabel={submitLabel}
                    disabled={
                        !hasChanges ||
                        (requireSelection && localSelected.size === 0)
                    }
                />
            )}
        </>
    );

    if (isInline) {
        return (
            <div className="flex h-[420px] w-full flex-col gap-6 @medium:h-[320px]">
                {content}
            </div>
        );
    }

    return (
        <Popover
            title={t('menu.title')}
            onClose={onClose}
            className="@container h-[470px] w-[730px] max-w-[calc(100vw-40px)] gap-6 p-4"
        >
            {content}
        </Popover>
    );
}
